import { useQuery } from '@tanstack/react-query'

import { API_GATEWAY, ENDPOINTS, USER_PROFILE_CONTRACT, USE_API_GATEWAY_FOR_PROFILE } from '@/constants/constants'
import { useSession } from '@/context/SessionProvider'

import {
  ChainGrpcAuthApi,
  ChainGrpcBankApi,
  ChainGrpcWasmApi,
  ChainRestAuthApi,
  ChainRestBankApi,
  ChainRestWasmApi,
  fromBase64,
  getEthereumAddress,
  toBase64,
} from '@ninjablaze/sdk-ts'
import { UserProfileContractQueryMsgBuilder } from './UserProfileContract.msg-builder'
import { User, UserProfile } from './UserProfileContract.types'

import { useQuery as useAppoloQuery } from '@apollo/client'
import { gql } from '@/__generated__/gql'
import { useMemo } from 'react'
import { getAnonymousUserName } from '@/helpers/getAnonymousUserName'

export const chainGrpcAuthApi = new ChainRestAuthApi(ENDPOINTS.rest)

export const chainGrpcBankApi = new ChainRestBankApi(ENDPOINTS.rest)

export const chainGrpcWasmApi = new ChainRestWasmApi(ENDPOINTS.rest)

export const fetchChainAccountByAddress = async (address: string | undefined) => {
  if (address === undefined) {
    return undefined
  }

  try {
    return await chainGrpcAuthApi.fetchAccount(address)
  } catch {
    return undefined
  }
}

export const fetchUserByUsername = async (username: string | undefined) => {
  if (username === undefined) {
    return undefined
  }

  const result = await chainGrpcWasmApi
    .fetchSmartContractState(
      USER_PROFILE_CONTRACT,
      toBase64(UserProfileContractQueryMsgBuilder.getUserByUserName({ userName: username }))
    )
    .then((res: any) => {
      return res.data as User & { address: string }
    })

  return result
}

export const fetchUserByAddress = async (address: string | undefined) => {
  if (address === undefined) {
    return undefined
  }

  const result = await chainGrpcWasmApi
    .fetchSmartContractState(
      USER_PROFILE_CONTRACT,
      toBase64(UserProfileContractQueryMsgBuilder.getUser({ addr: address! }))
    )
    .then((res: any) => {
      return res.data as User
    })
    .then((res) => {
      if (res?.profile?.user_name == address) {
        return {
          ...res,
          profile: {
            ...res!.profile,
            user_name: getAnonymousUserName(address),
          },
        }
      }
      return res
    })

  return result
}

export const fetchtUserProfileByAddress: (address: string | undefined) => Promise<UserProfile> = async (address) => {
  if (address === undefined) {
    return undefined
  }

  if (USE_API_GATEWAY_FOR_PROFILE) {
    const response = await fetch(`${API_GATEWAY}/users/${getEthereumAddress(address)}`)
    const result = await response.json()
    return result?.profile
  }

  const result = await chainGrpcWasmApi
    .fetchSmartContractState(
      USER_PROFILE_CONTRACT,
      toBase64(UserProfileContractQueryMsgBuilder.getUser({ addr: address }))
    )
    .then((res: any) => {
      return res.data as User
    })

  return result?.profile
}

export const useGetCurrentUserProfile = () => {
  const { account } = useSession()

  return useQuery({
    queryKey: ['USER_PROFILE_ME', account?.injectiveAddress],
    queryFn: () => fetchUserByAddress(account?.injectiveAddress),
    staleTime: Infinity,
    enabled: account ? true : false,
  })
}

export const useGetUserProfileByAddress = (address: string) => {
  const query = useAppoloQuery(GET_USER_PROFILE, {
    skip: !address,
    variables: {
      address: address,
    },
  })

  return useMemo(() => {
    if (query.loading) {
      return query
    }
    let data = { ...query.data }
    if (!data?.user) {
      data = {
        ...data,
        user: {
          profileUserName: getAnonymousUserName(address, 'Guest-'),
          profileAvatar: 'Qmdm3Fdw3fWbL8JN46ecbR2kkvcxT2D423FJwYbZn4hWR6',
          __typename: 'User',
        },
      }
    }
    return Object.freeze({
      ...query,
      data,
    })
  }, [query])
}

export const GET_USER_PROFILE = gql(`
  query GetUserProfile($address: String!) {
    nfts (condition: { owner: $address }, first: 1) {
      totalCount
    }
    user(id: $address) {
      profileUserName,
      profileAvatar,
    }
  }
`)

export const LAST_JACKPOT_GAMES_QUERY = gql(`
  query GetJackpotGames {
    jackpotGames (first: 7, orderBy: ID_DESC, condition: { state: "finished" }) {
      nodes {
        id
        playerWinner
        jackpotBetsByGameId {
          nodes {
            id
            address
            amount
          }
        }
        winAmount
        randomness
        startBlock
        created,
        drawTimestamp
      }
    }
  }
`)

export const LAST_JACKPOT_USER_GAMES_QUERY = gql(`
  query GetJackpotUserGames($address: String!, $cursor: Cursor) {
    jackpotUserGames (player: $address, first: 20, after: $cursor, filter: { state: { equalTo: "finished" } }) {
      pageInfo {
        hasNextPage
        endCursor
      }  
      edges {
        node {
          id
          playerWinner
          pool
          winAmount
          jackpotBetsByGameId {
            nodes {
              id
              address
              amount
            }
          }
        }
      }
    }
  }
`)

export const LAST_JACKPOT_GAME_DETAILS_QUERY = gql(`
  query GetJackpotGameDetails($id: BigInt!) {
    jackpotGame (id: $id) {
      id
      playerWinner
      jackpotBetsByGameId {
        nodes {
          id
          address
          amount
          txHash
        }
      }
      drawTxHash
      winAmount
      randomness
      startBlock
      created
      drawTimestamp
      drawRound
      state
      pool
    }
  }
`)

export const USER_LOOTBOX_ORDERS = gql(`
  query GetUserLootboxOrders($address: String!, $orderBy: [LootboxMarketOrdersOrderBy!]) {
    lootboxMarketOrders(condition: { player: $address, status: ACTIVE }, orderBy: $orderBy) {
      nodes {
        orderId
        lootbox
        price
        status
        type
      }
      totalCount
    }
  }
`)

export const USER_NUMBER_LOOTBOX_ORDERS = gql(`
  query GetUserNumberLootboxOrders($address: String!) {
    lootboxMarketOrders(condition: { player: $address, status: ACTIVE }) {
      totalCount
    }
  }
`)

export const USER_LAST_WINS = gql(`
  query GetUserLastWins($address: String!) {
    jackpotGames(
      orderBy: DRAW_TIMESTAMP_DESC
      filter: {playerWinner: {equalTo: $address}}
      first: 1
    ) {
      nodes {
        id
        drawTimestamp
        pool
        winAmount
        jackpotBetsByGameId(filter: { address: { equalTo: $address }}) {
          nodes {
            id
            address
            amount
          }
        }
      }
    }
    doubleUserGames(
      orderBy: GAME_ID_DESC,
      filter: { address: { equalTo: $address }, totalresult: { greaterThan: "0"}}
      first: 1
    ) {
      nodes{
        timestamp
        denom
        gameId
        totalpayout
        totalresult
        wincolor
        totalamount
      }
    }
    minesGames(filter: { player: { equalTo: $address}, status: { equalTo: WIN } }, first: 1, orderBy: UPDATED_DESC) {
      nodes {
        id
        payable
        bet
        denom
        updated
        txHash
      }
    }
  }
`)

export const GET_USER_STATS_DATA = gql(`
  query GetUserStatsData($address: String!) {
    nfts (condition: { owner: $address }) {
      totalCount
      nodes {
        type
      }
    }
    userStatisticsV2(player: $address) {
      betCount
      betAmount
      payoutCount
      payoutAmount
      maxPayout
      activeReferralsCount
    }
    user(id: $address) {
      profileUserName,
      profileAvatar,
      created,
      referrer
    }
  }
`)


export const GET_USER_REFERRAL_DETAILS = gql(`
  query GetUserReferralDetails($referrerAddress: String!) {
    users(filter: { referrer: { equalTo: $referrerAddress } }) {
      nodes {
        id
        profileUserName
        profileAvatar
        created
        modified
        userStatistic {
          totalBetAmount
          totalGameCount
        }
      }
      totalCount
    }
  }
`)

export const GET_PARTNER_USERS_STATS = gql(`
  query GetPartnerUsersStats($partnerAddress: String!, $startDate: Datetime!, $endDate: Datetime!) {
    getPartnerUsersStats(pPartnerAddress: $partnerAddress, pFrom: $startDate, pTo: $endDate) {
      nodes {
        userId
        profileUserName
        profileAvatar
        registeredAt
        lastBetAt
        betCount
        byDenom
      }
      totalCount
    }
  }
`)

export const GET_USER_LOOTBOX_GAMES = gql(`
  query GetUserLootboxGames($address: String!) {
    lootboxGames(filter: { status: { notEqualTo: FINISHED },  player: { equalTo: $address} }) {
      nodes {
        lootbox
        id
        txHash
        height
        created
        updated
        status
        drawnTxHash
        player
        randomness
        roundRandomness
        rewardId
      }
    }
  }
`)

export const GET_USER_UNCLAIMED_REWARDS = gql(`
  query GetUserUnclaimedRewards($lootbox: String!, $address: String!) {
      lootboxGames(
        filter: {status: {equalTo: DRAWN}, player: {equalTo: $address}, lootbox: {equalTo: $lootbox}}
      ) {
        nodes {
          id
          rewardId
        }
      }
    }
  `)

export const GET_ALL_USER_UNCLAIMED_REWARDS = gql(`
  query GetAllUserUnclaimedRewards($address: String!) {
      lootboxGames(
        filter: {status: {equalTo: DRAWN}, player: {equalTo: $address}}
      ) {
        nodes {
          id
          rewardId
          lootbox
        }
      }
    }
  `)

export const GET_LOOTBOX_V2_GAMES = gql(`
  query GetLootboxV2Games {
    lootboxUnlimitedGames(orderBy: ID_DESC, condition: { status: FINISHED  }, first: 9) {
      edges {
        node {
          id
          player
          lootbox
          rewards
          denom
          bet
          betUsd
        }
      }
    }
    statistic(id: 1) {
      lootboxBetCount
    }
  }
`)

export const GET_LOOTBOX_GAMES = gql(`
  query GetLootboxGames($limit: Int) {
    lootboxGames(orderBy: HEIGHT_DESC, filter: { status: { equalTo: FINISHED } }, first: $limit) {
      nodes {
        lootbox
        id
        txHash
        height
        created
        updated
        status
        drawnTxHash
        player
        randomness
        roundRandomness
        rewardId
      }
    }
    statistic(id: 1) {
      lootboxBetCount
  	}
  }
`)

export const GET_LOOTBOXES_SALES = gql(`
  query GetLootboxesSales {
    lootboxSales {
      nodes {
        id
        salesCount
      }  
    }
  }
`)

export const GET_LOOTBOX_SALES = gql(`
  query GetLootboxSales($id: String!) {
      lootboxSale(id: $id) {
        id
        salesCount
      }
    }
`)

export const GET_LOOTBOXES_GAMES_COUNT = gql(`
  query GetLootboxesGamesCount {
    lootboxes {
      edges {
        node {
          id
          gameCount
        }
      }
    }
  }
`)

export const GET_LOOTBOX_TRADES = gql(`
  query GetLootboxTrades($denom: String!, $limit: Int) {
    lootboxMarketTrades(orderBy: TRADE_ID_DESC, condition:{ lootbox: $denom }, first: $limit) {
      nodes {
        buyer
        price
        tx
        created
      }
    }
  }
`)

export const GET_LOOTBOX_GAME_COUNT = gql(`
  query GetLootboxGameCount($id: String!) {
    lootbox(id: $id) {
      gameCount
    }
  }
`)

// export const GET_LOOTBOX_USER_ORDERBOOK = gql(`
//   query GetLootboxUserOrderbook($id: String!) {
//     lootboxMarketOrders (orderBy: PRICE_ASC filter: { lootbox: { equalTo: $id } , quote: { equalTo: "inj"}, player: { equalTo: "" }, status: { equalTo: ACTIVE } }) {
//       nodes {
//         orderId
//         player
//         type
//         price
//         lootboxAmount
//         executedAmount
//       }
//     }
//   }
// `)

export const GET_LOOTBOX_ORDERBOOK = gql(`
  query GetLootboxOrderbook($id: String!) {
    buy: lootboxOrderBook(lootbox: $id, quote: "inj", type: BUY) {
      nodes {
        price
        quantity
      }
    }
    sell: lootboxOrderBook(lootbox: $id, quote: "inj", type: SELL) {
      nodes {
        price
        quantity
      }
    }
  }
`)

export const USER_BET_HISTORY = gql(`
query GetUserGames($address: String!, $cursor: Cursor, $game: String!) {
  betsHistories(after: $cursor, filter: { player: { equalTo: $address }, game: { equalTo: $game } }, first: 20, orderBy: _ID_DESC) {
    pageInfo {
      hasNextPage
      endCursor
    }  
    edges {
      node {
        game
        player
        denom
        betId
        createdAt
        payout
        bet
        payoutUsd
        betUsd
        gameId
        extra
      }
    }
  }
}
`)

export const LAST_ROULETTE_GAMES_QUERY = gql(`
query GetDoubleGames($address: String!, $cursor: Cursor) {
  doubleUserGames(after: $cursor, filter: { address: { equalTo: $address } }, first: 20) {
    pageInfo {
      hasNextPage
      endCursor
    }  
    edges {
      node {
        gameId
        totalpayout
        totalamount
        denom
        red
        black
        green
        wincolor
        totaljackpot  
      }
    }
  }
}
`)

export const LAST_ROULETTE_GAME_DETAIL_QUERY = gql(`
query GetDoubleGameDetails($id: BigInt!, $address: String!) {
    doubleGame(id: $id) {
      id,
      state,
      drawTimestamp,
      randomness,
      drawRound
      drawTxHash
      winColor
      startBlock
      created
      jackpot
      doubleBetsByGameId {
        nodes {
          amount
          address
          denom
          bet
          jackpotPayout
          color
        }
      }
    }
    doubleUserGames(filter: { address: { equalTo: $address }, gameId: { equalTo: $id } } ) {
        nodes {
          red
          black
          green
          totaljackpot
          totalamount
          totalpayout      
        }
      }      
}`)

export const LAST_ROULETTE_GAME_BY_DENOM_DETAIL_QUERY = gql(`
  query GetDoubleGameByDenomDetails($id: BigInt!, $address: String!, $denom: String!) {
      doubleGame(id: $id) {
        id,
        state,
        drawTimestamp,
        randomness,
        drawRound
        drawTxHash
        winColor
        startBlock
        created
        jackpot
        doubleBetsByGameId {
          nodes {
            amount
            address
            denom
            jackpotPayout
            color
          }
        }
      }
      doubleUserGames(filter: { address: { equalTo: $address }, gameId: { equalTo: $id }, denom: { equalTo: $denom } }) {
          nodes {
            red
            black
            green
            totaljackpot
            totalamount
            totalpayout      
          }
        }      
  }`)

export const MINES_GAMES_HISTORY_QUERY = gql(`
  query GetUserMinesHistoryGame($address: String!, $cursor: Cursor) {
    minesGames (orderBy: ID_DESC, after: $cursor, filter: { player: { equalTo: $address }, , status: { notEqualTo: PENDING }  }, first: 20) {
      pageInfo {
        hasNextPage
        endCursor
      }  
      edges {
        node {
          id
          player
          bet
          denom
          payable
          status
          mines
          maxCells
          tryGemsOpened
          gemsOpened
          theoreticalGemsOpened
        }
      }
    }
  }
  `)

export const LAST_MINE_GAMES_QUERY = gql(`
  query GetMineGames {
    minesGames(filter:{ status: { notEqualTo: PENDING }}, first: 11, orderBy: UPDATED_DESC) {
      nodes {
        id
        player
        bet
        denom
        payable
        status
        betUsd
        payoutUsd
      }
    }
  }
`)

export const LAST_MINE_GAMES_BY_PLAYER_QUERY = gql(`
  query GetMineGamesByPlayer($player: String!) {
    minesGames(filter:{ status: { notEqualTo: PENDING }, player: { equalTo: $player }}, first: 11, orderBy: UPDATED_DESC) {
      nodes {
        id
        player
        bet
        denom
        payable
        status
        betUsd
        payoutUsd
      }
    }
  }
`)

export const LOOTBOX_COMPETITION_USER_QUERY = gql(`
  query GetUserLootboxCompetitionScore($player: String!) {
    openCompetitionLeaderBoards(filter: { player: { equalTo: $player } }) {     
      nodes {
        boxOpen
        score
        refererScore
        hasNft
      }
    }
  }
`)

export const LOOTBOX_COMPETITION_JACKPOT_USER_QUERY = gql(`
  query GetUserLootboxCompetitionJackpotScore($player: String!) {
    openCompetitionEntity(player: $player) {
      jackpotScore
      score
      refererScore
      doubleScore
    }
  }
`)

export const LOOTBOX_COMPETITION_QUERY = gql(`
  query GetUserLootboxCompetitionLeaderboard($player: String!) {
    userProfile: user(id: $player ) {     
      created
    }
    leaderboard: openCompetitionLeaderBoards(first: 100) {
      nodes {
        player
        boxOpen
        score
        refererScore
        hasNft
        fullScore
        totalScore
        rank
        nfts
      }
    }
    user: openCompetitionLeaderBoards(filter: { player: { equalTo: $player } }) {     
      nodes {
        score
        totalScore
        fullScore
        hasNft
        rank
        nfts
      }
    }
    userEntity: openCompetitionEntity(player: $player) {     
      score
      refererScore
      jackpotScore
      doubleScore
    }
  }
`)

export const MINES_GAME_DETAILS_QUERY = gql(`
  query GetMinesGameDetails($id: BigInt!) {
    minesGame (id: $id) {
      id
      player
      bet
      payable
      status
      mines
      denom
      maxCells
      tryGemsOpened
      minesLast
      rtp
      gemsOpened
      created
      txHash
      theoreticalGemsOpened
      minesGameRoundsByGameId {
        nodes {
          userChoice
          mines
          result
        }
      }
    }
}`)

export const GET_USER_MINE_GAME = gql(`
  query GetUserMineGame($player: String!) {
    minesGames (condition: { player: $player, status: PENDING }, first: 1, orderBy: [ID_DESC]) {
      nodes {
        id
        bet,
        denom,
        mines,
        rtp,
        maxCells,
        maxWin,
      }
    }
  }`)

export const GET_MINE_GAME = gql(`
    query GetMineGame($id: BigInt!) {
      minesGame (id: $id) {
          id
          bet,
          denom,
          mines,
          minesGameRoundsByGameId {
            nodes {
              id
              result
            }
          }
        }
    }`)

export const GET_AUCTION_BASKETS = gql(`
  query GET_AUCTION_BASKETS {
    auctionBaskets(first: 5, orderBy: ID_DESC) {
      nodes {
        id,
        basket
        status
        bet
        leader
        created
        end
        auctionBasketBets {
          totalCount
          nodes {
            id
            bet
            player
            created
            txHash
          }
        }
      }
    }
    statistic(id: 1) {
      auctionBurnToken
    }
    tokenPrices {
      nodes {
        symbol
        value
        updated
      }
    }
  }
`)

export const GET_AUCTION_BASKET_BY_ID = gql(`
  query GET_AUCTION_BASKET_BY_ID($id: BigInt!) {
    auctionBasket(id: $id) {
      id,
      basket
      status
      bet
      leader
      created
      end
      auctionBasketBets {
        totalCount
        nodes {
          id
          bet
          player
          created
          txHash
        }
      }
    },
    auctionBaskets(first: 5, orderBy: ID_DESC) {
      nodes {
        id,
        basket
        status
        bet
        leader
        created
        end
        auctionBasketBets {
          totalCount
          nodes {
            id
            bet
            player
            created
            txHash
          }
        }
      }
    }
    statistic(id: 1) {
      auctionBurnToken
    }
    tokenPrices {
      nodes {
        symbol
        value
        updated
      }
    }
  }
`)

export const GET_COINFLIP_GAME = gql(`
  query GET_COINFLIP_GAME($id: BigInt!) {
    coinflipGame(id: $id) {
      id
      gameRound
      processedGameRound
      player
      status
      txHash
      bet
      payable
      rtp
      maxWin
      maxGameRound
      denom
      mode
      updated
      coinflipGameRoundsByGameId {
        nodes {
          userChoice
          result
        }
      }
    }
  }`)

export const GET_COINFLIP_GAMES = gql(`
query GET_LAST_COINFLIP_GAMES {
  coinflipGames(
    orderBy: ID_DESC
    filter: {or: [{status: {equalTo: WIN}}, {status: {equalTo: LOSE}}]}
    first: 11
  ) {
    nodes {
      id
      player
      bet
      payable
      status
      denom
      betUsd
      payoutUsd
    }
  }
  appCoinflipLeaderboardLuckyBets {
    nodes {
      id
      player
      bet
      payable
      status
      denom
      betUsd
      payoutUsd
    }
  }
}
`)

export const GET_COINFLIP_GAMES_BY_PLAYER = gql(`
query GET_LAST_COINFLIP_GAMES_BY_PLAYER($player: String!) {
  coinflipGames(
    orderBy: ID_DESC
    filter: {player: {equalTo: $player}, or: [{status: {equalTo: WIN}}, {status: {equalTo: LOSE}}]}
    first: 11
  ) {
    nodes {
      id
      player
      bet
      payable
      status
      denom
      betUsd
      payoutUsd
    }
  }
}
`)

export const GET_COINFLIP_USER_SCORE = gql(`
  query GET_COINFLIP_USER_SCORE($address: String!) {
    userCompetition(player: $address, competition: "coinflip-100000") {
      score
    }
  }
`)

export const GET_LAST_COINFLIP_GAME_BY_PLAYER = gql(`
    query GET_LAST_COINFLIP_GAME_BY_PLAYER($player: String!) {
      coinflipGames(condition: { player: $player, status: PENDING, mode: MULTIPLE }, orderBy: ID_ASC) {
        nodes {
          id
          gameRound
          processedGameRound
          maxGameRound
          denom
          bet
          rtp
          maxWin
          coinflipGameRoundsByGameId {
            nodes {
              userChoice
              result
            }
          }
        }
      }
    }
  `)

export const GET_RUNES_GAMES = gql(`
query GET_LAST_RUNES_GAMES {
  runesGames(
    orderBy: ID_DESC
    filter: { status: { equalTo: FINISHED }},
    first: 11
  ) {
    nodes {
      id
      risk
      player
      bet
      payout
      denom
      betUsd
      payoutUsd
    }
  }
}`)

export const GET_RUNES_GAMES_BY_PLAYER = gql(`
query GET_LAST_RUNES_GAMES_BY_PLAYER($player: String!) {
  runesGames(
    orderBy: ID_DESC
    filter: { status: { equalTo: FINISHED }, player: { equalTo: $player }},
    first: 11
  ) {
    nodes {
      id
      risk
      player
      bet
      payout
      denom
      betUsd
      payoutUsd
    }
  }
}`)

export const GET_RUNES_GAME = gql(`
query GET_RUNES_GAME($id: BigInt!) {
    runesGame(
    id: $id
  ) {
    id,
    player,
    txHash,
    bet,
    payout,
    denom
    created,
    risk,
    result
  }
}`)

export const GET_PLINKO_TICKETS_LEADERBOARD = gql(`
  query GET_PLINKO_TICKETS_LEADERBOARD {
    userCompetitions(filter: { competition: { equalTo: "plinko"}}, first: 100, orderBy:SCORE_DESC) {
      nodes {
        player,
        score
      }
    }
  }
`)

export const GET_PLINKO_TICKETS_BY_USER = gql(`
  query GET_PLINKO_TICKETS_BY_USER($address: String!) {
    userCompetition(player: $address, competition: "plinko") {
      player,
      score
    }
  }
`)

export const GET_LAST_WINS = gql(`
    query GET_LAST_WINS($start: Datetime!) {
      infoLast100WinBets(filter: { createdAt: { greaterThan: $start }}) {
        nodes {
          id
          player
          denom
          payout
          payoutUsd
          createdAt
          game,
          extraGameId
        }
      }
    }
  `)

export const GET_USERS_BY_NAME = gql(`
  query GET_USERS_BY_NAME($name: String!) {
    users(filter: {profileUserName: {includesInsensitive: $name}}, first: 10) {
      nodes {
        id
        profileUserName
        profileAvatar
      }
    }
  }
`)


export const GET_PLINKO_GAME = gql(`
  query GET_PLINKO_GAME($id: BigInt!) {
    plinkoGame(id: $id) {
      id
      player
      denom
      bet
      balls
      payout
      risk
      result
      status
      txHash
      created
      updated
    }
  }
`)

export const GET_PLINKO_GAMES = gql(`
query GET_LAST_PLINKO_GAMES {
  plinkoGames(orderBy: ID_DESC, filter: {status: { equalTo: FINISHED }}, first: 20) {
    nodes {
      id
      player
      denom
      bet
      balls
      payout
      risk
      betUsd
      payoutUsd
    }
  }
}
`)

export const GET_PLINKO_GAMES_BY_PLAYER = gql(`
query GET_LAST_PLINKO_GAMES_BY_PLAYER($player: String!) {
  plinkoGames(orderBy: ID_DESC, filter: {status: { equalTo: FINISHED }, player: { equalTo: $player }}, first: 20) {
    nodes {
      id
      player
      denom
      bet
      balls
      payout
      risk
      betUsd
      payoutUsd
    }
  }
}
`)

export const TOKEN_PRICES_QUERY = gql(`
  query GameZoneSessionTokenPrices {
    tokenPrices {
      nodes {
        symbol
        value
      }
    }
  }
`)